/**
 * Q3 — "What's not working?" (Q4 section in the new Overview).
 *
 * Mirror of Q2: the worst 7 elements by scale-aware grade (computed in
 * buildMarketerViewData) — 3 hero cards at the top + 4 in a trailing
 * "The rest" card. Worst (red) tone throughout.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { MarketerViewData, ScoreNode } from '../types';
import { BAND_PALETTE, formatBipolarScore } from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import { BandTag } from '../atoms';
import { Card } from '../atoms';
import { Eyebrow } from '../atoms';
import { SignalBar } from '../atoms';
import { LockedOverlay } from '../atoms/LockedOverlay';
import { SectionHead } from './SectionHead';
import { firstThoughtsSnippet } from '../config/placeholderNarrative';

export interface Q3Props {
  data: MarketerViewData;
  onOpen: (node: ScoreNode) => void;
  /** Section badge override (the new Overview reorders this to Q4). */
  q?: string;
  /** Tier-locked: blur the body behind an upgrade CTA (title stays visible). */
  locked?: boolean;
}

export const Q3: React.FunctionComponent<Q3Props> = ({ data, onOpen, q = 'Q3', locked = false }) => {
  // Worst 7 elements by scale-aware grade: 3 hero cards + 4 in "The rest".
  const top = data.worstElements.slice(0, 3);
  const rest = data.worstElements.slice(3, 7);

  return (
    <section id="broken" style={{ paddingTop: 50 }}>
      <SectionHead
        q={q}
        tone={BAND_PALETTE.weak.fg}
        title={<FormattedMessage id="marketerView.q3.title" defaultMessage="What's not working?" />}
        sub={
          <FormattedMessage
            id="marketerView.q3.sub"
            defaultMessage="The elements currently scoring weakest across the brand. They are not necessarily bad, but are dragging on overall equity and should lead your fix list."
          />
        }
      />
      <LockedOverlay locked={locked}>
      <p style={{ fontSize: 14, color: '#64748B', margin: '0 0 18px', maxWidth: 720 }}>
        {data.brokenFraming}
      </p>
      {top.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 18,
          }}
        >
          {top.map((s) => (
            <Card
              key={s.canonical}
              className="lift"
              style={{ borderTop: `3px solid ${BAND_PALETTE.weak.fg}` }}
              onClick={() => onOpen(s)}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: 36,
                    fontWeight: 600,
                    color: colourForScore(s.canonical, s.value),
                    letterSpacing: '-0.03em',
                  }}
                >
                  {formatBipolarScore(s.value)}
                </span>
                <BandTag classification={s.classification} band={s.band} />
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#0F172A',
                  marginTop: 10,
                }}
              >
                {s.display}
              </div>
              <p
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: '#64748B',
                  marginTop: 8,
                }}
              >
                {firstThoughtsSnippet(s.aiSummary) || (
                  <FormattedMessage
                    id="brandEquity.aiSummary.awaitingResponses"
                    defaultMessage="Awaiting more responses before forming a view."
                  />
                )}
              </p>
            </Card>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <Card style={{ marginTop: 18 }}>
          <Eyebrow color={BAND_PALETTE.weak.fg}>
            <FormattedMessage id="marketerView.q3.restEyebrow" defaultMessage="The rest" />
          </Eyebrow>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '2px 36px',
              marginTop: 10,
            }}
          >
            {rest.map((s) => (
              <SignalBar key={s.canonical} node={s} onClick={() => onOpen(s)} />
            ))}
          </div>
        </Card>
      )}
      </LockedOverlay>
    </section>
  );
};
